export const ONBOARDING_TOUR_MODES = ['auto', 'force', 'off'] as const
export type OnboardingTourMode = (typeof ONBOARDING_TOUR_MODES)[number]

export const ONBOARDING_EXPERIENCES = ['familiar', 'newbie'] as const
export type OnboardingExperience = (typeof ONBOARDING_EXPERIENCES)[number]

export const ONBOARDING_STATUSES = ['pending', 'in_progress', 'skipped', 'completed'] as const
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number]

export const ONBOARDING_STEPS = [
  'account-menu',
  'your-profile',
  'product-nav',
  'create-menu',
  'new-repository',
  'create-name',
  'create-visibility',
  'create-submit',
  'import-ask',
  'import-tab',
  'import-url',
  'import-name',
  'import-visibility',
  'import-backup',
  'import-submit',
  'search',
  'done',
] as const

export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]

export const CREATE_FORM_STEPS = ['create-name', 'create-visibility', 'create-submit'] as const
export const IMPORT_FORM_STEPS = [
  'import-tab',
  'import-url',
  'import-name',
  'import-visibility',
  'import-backup',
  'import-submit',
] as const

export const TOUR_TARGET = {
  accountMenu: '[data-tour="account-menu"]',
  yourProfile: '[data-tour="your-profile"]',
  productNav: '[data-tour="product-nav"]',
  createMenu: '[data-tour="create-menu"]',
  newRepository: '[data-tour="new-repository"]',
  repoName: '[data-tour="repo-name"]',
  repoVisibility: '[data-tour="repo-visibility"]',
  createRepoSubmit: '[data-tour="create-repo-submit"]',
  importTab: '[data-tour="import-tab"]',
  importUrl: '[data-tour="import-url"]',
  importName: '[data-tour="import-name"]',
  importVisibility: '[data-tour="import-visibility"]',
  importBackup: '[data-tour="import-backup"]',
  importSubmit: '[data-tour="import-submit"]',
  globalSearch: '[data-tour="global-search"]',
  searchSyntaxHelp: '[data-tour="search-syntax-help"]',
} as const

export interface OnboardingState {
  experience: OnboardingExperience | null
  status: OnboardingStatus | null
  currentStep: OnboardingStepId | null
}

export function isOnboardingTourMode(
  raw: string | undefined = process.env.ONBOARDING_TOUR,
): OnboardingTourMode {
  if (raw === undefined || raw.trim() === '') return 'auto'
  const normalized = raw.trim().toLowerCase()
  if (normalized === 'auto' || normalized === 'force' || normalized === 'off') {
    return normalized
  }
  throw new Error(`Unknown ONBOARDING_TOUR "${raw}". Use auto, force, or off.`)
}

export function isOnboardingExperience(value: unknown): value is OnboardingExperience {
  return value === 'familiar' || value === 'newbie'
}

export function isOnboardingStatus(value: unknown): value is OnboardingStatus {
  return (
    value === 'pending' ||
    value === 'in_progress' ||
    value === 'skipped' ||
    value === 'completed'
  )
}

export function isOnboardingStepId(value: unknown): value is OnboardingStepId {
  return typeof value === 'string' && (ONBOARDING_STEPS as readonly string[]).includes(value)
}

export function shouldStartOnboardingTour({
  mode,
  status,
  queryForce = false,
}: {
  mode: OnboardingTourMode
  status: OnboardingStatus | null
  queryForce?: boolean
}): boolean {
  if (mode === 'off') return false
  if (mode === 'force' || queryForce) return true
  return status === 'pending' || status === 'in_progress'
}

export function initialOnboardingPhase(
  status: OnboardingStatus | null,
): 'welcome' | 'tour' {
  return status === 'in_progress' ? 'tour' : 'welcome'
}

export function requiredPathForStep(step: OnboardingStepId): string | null {
  if ((CREATE_FORM_STEPS as readonly string[]).includes(step)) return '/new'
  if ((IMPORT_FORM_STEPS as readonly string[]).includes(step)) return '/new?mode=import'
  return null
}

export function isOnRequiredPath(
  pathname: string,
  search: string,
  step: OnboardingStepId,
): boolean {
  const required = requiredPathForStep(step)
  if (!required) return true
  const url = new URL(required, 'http://kilio.local')
  if (pathname !== url.pathname) return false
  const needImport = url.searchParams.get('mode') === 'import'
  const haveImport = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    'mode',
  ) === 'import'
  return needImport === haveImport
}

export function includeImportFormSteps(step: OnboardingStepId | null): boolean {
  return Boolean(step && (IMPORT_FORM_STEPS as readonly string[]).includes(step))
}

export function stepsToDrive(includeImport: boolean): OnboardingStepId[] {
  if (includeImport) return [...ONBOARDING_STEPS]
  return ONBOARDING_STEPS.filter((step) => !(IMPORT_FORM_STEPS as readonly string[]).includes(step))
}

export function isCreateFormStep(step: OnboardingStepId): boolean {
  return (CREATE_FORM_STEPS as readonly string[]).includes(step)
}

export function isImportFormStep(step: OnboardingStepId): boolean {
  return (IMPORT_FORM_STEPS as readonly string[]).includes(step)
}
